import { redirect } from "react-router";
import { LoaderFunctionArgs } from "react-router-dom";
import { isAgentBoardEnabled } from "@/configs/agentFeatures";
import { ACTIVE } from "@/configs/constants";
import { Navigations, resolveDefaultLandingPath, URLs } from "@/configs/menu";
import { UserContext } from "@/context/UserContext";
import {
  compileNavigationRoutes,
  compileRouteRegex,
  computeNavigationItem,
  findOwningNavigation,
  flattenNavigations,
} from "@/layout/v3/Navigation";
import {
  clearAuthStorage,
  isTokenExpired,
  setSessionExpired,
} from "@/libs/store";

// Navigations is a static module-level config, so neither its flattened form
// nor the compiled patterns change at runtime - build them once instead of on
// every navigation.
const routableNavigations = compileNavigationRoutes(
  flattenNavigations(Navigations),
);

// Compiled once for the same reason: `publicRouteLoader` tests both on every
// public navigation.
const LOGOUT_PATTERNS = [URLs.aws_singUp, URLs.register_email_verified].map(
  compileRouteRegex,
);

/**
 * Checks that the user is logged in and the stored token has not expired.
 * On expiry, clears stale auth storage so public routes and the App
 * auto-login effect treat the user as logged out (no redirect loop), and
 * flags the session as expired so the login page shows its notice.
 */
function hasValidSession(): boolean {
  const user = UserContext.getUser();
  const idToken = UserContext.getToken();

  if (!user.userId || !idToken) {
    return false;
  }

  if (isTokenExpired()) {
    setSessionExpired();
    clearAuthStorage();
    return false;
  }

  return true;
}

/**
 * Loader for authenticated routes - redirects to login if not authenticated
 */
export async function requireAuthLoader() {
  if (!hasValidSession()) {
    return redirect("/login");
  }

  return null;
}

/**
 * Loader to check if the feature and authority is enabled for the current user
 * @param props - LoaderFunctionArgs
 * @returns null if the feature and authority is enabled, otherwise redirects to the root path
 */
export async function checkFeatureAndAuthorityLoader(
  props: LoaderFunctionArgs,
) {
  const url = new URL(props.request.url);
  const pathname = url.pathname;

  const navigationEntry = findOwningNavigation(pathname, routableNavigations);

  // If no navigation entry found, allow access (might be a route not in navigation)
  if (!navigationEntry) {
    return null;
  }

  const user = UserContext.getUser();
  // ACTIVE only, matching `useAccounts(true)` in the sidebar and the
  // Administration hub - an entry's `accounts` gate must resolve the same way
  // here as it does in the UI that links to it.
  const activeAccounts = (user.accounts ?? []).filter(
    account => account.accountStatus === ACTIVE,
  );
  const resolvedEntry = computeNavigationItem(
    navigationEntry,
    activeAccounts,
    // Bound to the user already in hand: `UserContext.hasAuthority` re-parses
    // the whole user blob out of localStorage per call, and resolving an
    // Administration entry checks every descendant.
    UserContext.authorityChecker(user),
    { user, isAuthenticated: true },
    // Overrides included, matching `useConfigParameters` in the sidebar.
    UserContext.getConfigParameters(user),
    // Route access, not sidebar display: `hiddenByFeatureConfig` hides the link only.
    true,
  );

  if (!resolvedEntry.display) {
    return redirect(URLs.root);
  }

  return null;
}

/**
 * Combined loader for authenticated routes that checks both authentication and feature/authority
 * This ensures the user is authenticated and has the required permissions before accessing the route
 * @param props - LoaderFunctionArgs
 * @returns null if all checks pass, otherwise redirects appropriately
 */
export async function authenticatedRouteLoader(props: LoaderFunctionArgs) {
  // First check authentication
  const authResult = await requireAuthLoader();
  if (authResult) {
    return authResult;
  }

  // Then check feature and authority
  return checkFeatureAndAuthorityLoader(props);
}

/**
 * Loader for public routes - redirects authenticated users away from login/signup pages
 * Also handles logout flow for specific paths
 */
export async function publicRouteLoader({ request }: { request: Request }) {
  const url = new URL(request.url);
  const pathname = url.pathname;

  const user = UserContext.getUser();

  const isAuthenticated = hasValidSession();

  // Check if current path is a logout path. Exact match, deliberately not
  // prefix-based: `/setup/:token/aws` shares a static base with the unrelated
  // public route `/setup/free_trial`, which a prefix match would swallow.
  const isLogoutPath = LOGOUT_PATTERNS.some(pattern => pattern.test(pathname));

  // If authenticated and on logout path, allow it (logout will be handled by component)
  if (isAuthenticated && isLogoutPath) {
    return null;
  }

  // If authenticated and not on logout path, redirect to home
  if (isAuthenticated && !isLogoutPath) {
    // Compute default landing path
    try {
      const hasAccounts = Boolean(user.accounts?.length);
      const isAIModeEnabled = UserContext.hasConfigParameter("isTrustDSPM");

      // Check authority
      const hasCopilotAuthority = UserContext.hasAuthority(
        "TRUSTAI_COPILOT:READ",
      );

      const defaultLandingPath = resolveDefaultLandingPath({
        hasAccounts,
        isAgentBoardEnabled: isAgentBoardEnabled(),
        isAILanding: isAIModeEnabled && hasCopilotAuthority,
      });

      return redirect(defaultLandingPath);
    } catch {
      return redirect(URLs.dashboard);
    }
  }

  return null;
}

/**
 * Loader for root route - redirects to default landing path
 */
export async function rootRouteLoader() {
  if (!hasValidSession()) {
    return redirect("/login");
  }

  const user = UserContext.getUser();

  try {
    const hasAccounts = Boolean(user.accounts?.length);
    const isAIModeEnabled = UserContext.hasConfigParameter("isTrustDSPM");

    // Check authority from user data
    const hasCopilotAuthority = UserContext.hasAuthority(
      "TRUSTAI_COPILOT:READ",
    );

    const defaultLandingPath = resolveDefaultLandingPath({
      hasAccounts,
      isAgentBoardEnabled: isAgentBoardEnabled(),
      isAILanding: isAIModeEnabled && hasCopilotAuthority,
    });

    return redirect(defaultLandingPath);
  } catch {
    return redirect(URLs.dashboard);
  }
}
